import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import moment from 'moment';

import { Modal } from 'atoms';
import { PopupContainer } from 'molecules';
import { useChangeAgreementGroup, useGroupOptionsLoader } from 'core/hooks';
import type { IGroupPickerOption } from 'core/hooks';

import * as L from './detail.styled';

/**
 * Minimal shape needed to render the popup — keeps this file from
 * depending on either student- or group-page type projections. Both
 * `StudentDetail.parts.types.IAgreement` and the group roster's
 * agreement projection satisfy it structurally.
 */
export interface IAgreementForMove {
  id: string;
  currency?: string | null;
  agreementTitle?: string | null;
  group?: { id: string; name: string } | null;
}

interface IMoveToGroupInlinePopupProps {
  agreement: IAgreementForMove;
  studentId: string;
  onClose: () => void;
  onMoved?: () => void;
}

type IGroupOption = IGroupPickerOption;

/**
 * §6 inline popup: move the student's agreement from one group to another
 * atomically. Bypasses the global popup registry because the action is
 * hyper-local to one card/row (an `AgreementCard` on StudentDetail or a
 * `StudentRosterRow` on GroupDetail).
 *
 * Server contract — `ChangeAgreementGroupInput`:
 *   { agreementId, newGroupId, effectiveDate, copyPayments?, copyContributions? }
 *
 * The form blocks submit when the picked group is the current one or has
 * a different currency (CURRENCY_MISMATCH server error short-circuited
 * client-side so the operator sees the reason without a roundtrip).
 *
 * Translations: reuses the `pages.studentDetail.moveGroup.*` namespace
 * — the copy talks about «moving an agreement to a group», nothing
 * student-page-specific.
 */
export const MoveToGroupInlinePopup = memo(
  ({
    agreement,
    studentId,
    onClose,
    onMoved,
  }: IMoveToGroupInlinePopupProps) => {
    const { t } = useTranslation();
    const { move, loading } = useChangeAgreementGroup(studentId);
    // Поиск группы — общий с «Исправить группу» (ERP-110).
    const { loadOptions: loadGroupOptions } = useGroupOptionsLoader();

    const [group, setGroup] = useState<IGroupOption | null>(null);
    const [effectiveDate, setEffectiveDate] = useState<string>(
      moment().format('YYYY-MM-DD'),
    );
    // Default copyPayments OFF to match the backend norm (finance usually
    // keeps the ledger where the money was received). copyContributions ON is
    // the common case.
    const [copyPayments, setCopyPayments] = useState(false);
    const [copyContributions, setCopyContributions] = useState(true);
    // Post-move next-step panel (the zeroing/carry-out follow-up is otherwise
    // implicit). Set after a successful move instead of closing immediately.
    const [moved, setMoved] = useState(false);

    const currencyMismatch = useMemo(() => {
      if (!group || !group.currency) return false;
      return group.currency !== agreement.currency;
    }, [group, agreement.currency]);

    const sameGroup = useMemo(
      () => Boolean(group && group.value === agreement.group?.id),
      [group, agreement.group?.id],
    );

    const canSubmit = Boolean(
      group && effectiveDate && !currencyMismatch && !sameGroup && !loading,
    );

    const onSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        if (!group || !canSubmit) return;
        const ok = await move({
          agreementId: agreement.id,
          newGroupId: group.value,
          effectiveDate,
          copyPayments,
          copyContributions,
        });
        if (ok) {
          onMoved?.(); // refresh the card so it now reads «Переведён»
          setMoved(true); // show the follow-up checklist, don't close yet
        }
      },
      [
        group,
        canSubmit,
        move,
        agreement.id,
        effectiveDate,
        copyPayments,
        copyContributions,
        onMoved,
      ],
    );

    return (
      <Modal>
        <PopupContainer
          width="520px"
          title={t('pages.studentDetail.moveGroup.title')}
          onClose={onClose}
        >
          {moved ? (
            <L.FormBody>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--k-fg-047857)',
                }}
              >
                ✓ {t('pages.studentDetail.moveGroup.doneTitle')}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--k-fg-475569)',
                  lineHeight: 1.6,
                  background: 'var(--k-bg-f5f3ff)',
                  border: '1px solid var(--k-border-ddd6fe)',
                  borderRadius: 10,
                  padding: '12px 16px',
                }}
              >
                {t('pages.studentDetail.moveGroup.nextStep1')}
                <br />
                {t('pages.studentDetail.moveGroup.nextStep2')}
                <br />
                {t('pages.studentDetail.moveGroup.nextStep3')}
              </div>
              <L.FormFooter>
                <L.PrimaryActionButton
                  type="button"
                  tone="primary"
                  onClick={onClose}
                >
                  {t('pages.studentDetail.moveGroup.gotIt')}
                </L.PrimaryActionButton>
              </L.FormFooter>
            </L.FormBody>
          ) : (
            <form onSubmit={onSubmit}>
              <L.FormBody>
                <div style={{ fontSize: 13, color: 'var(--k-fg-475569)' }}>
                  {t('pages.studentDetail.moveGroup.fromHint', {
                    agreement: agreement.agreementTitle,
                    group: agreement.group?.name || '—',
                  })}
                </div>

                <L.FormLabel>
                  {t('pages.studentDetail.moveGroup.targetGroup')}
                  <AsyncSelect
                    autoFocus
                    cacheOptions
                    defaultOptions
                    value={group}
                    onChange={(v) => setGroup((v as IGroupOption) || null)}
                    loadOptions={loadGroupOptions}
                    placeholder={t(
                      'pages.studentDetail.moveGroup.searchPlaceholder',
                    )}
                  />
                  {currencyMismatch ? (
                    <L.InlineNotice>
                      {t('pages.studentDetail.moveGroup.currencyMismatch', {
                        from: agreement.currency,
                        to: group?.currency,
                      })}
                    </L.InlineNotice>
                  ) : null}
                  {sameGroup ? (
                    <L.InlineNotice>
                      {t('pages.studentDetail.moveGroup.sameGroup')}
                    </L.InlineNotice>
                  ) : null}
                </L.FormLabel>

                <L.FormLabel>
                  {t('pages.studentDetail.moveGroup.effectiveDate')}
                  <L.FormInput
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    required
                  />
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--k-fg-5b6775)',
                      fontWeight: 500,
                    }}
                  >
                    {t('pages.studentDetail.moveGroup.effectiveDateHint')}
                  </span>
                </L.FormLabel>

                <label
                  htmlFor="moveGroup-copyPayments"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    id="moveGroup-copyPayments"
                    type="checkbox"
                    checked={copyPayments}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setCopyPayments(v);
                      // Conservation invariant: moving money requires cloning the
                      // schedule (the backend rejects copy_payments without it).
                      if (v) setCopyContributions(true);
                    }}
                  />
                  {t('pages.studentDetail.moveGroup.copyPayments')}
                </label>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--k-fg-5b6775)',
                    marginTop: -6,
                  }}
                >
                  {t('pages.studentDetail.moveGroup.copyPaymentsHint')}
                </span>

                <label
                  htmlFor="moveGroup-copyContributions"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    cursor: copyPayments ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    id="moveGroup-copyContributions"
                    type="checkbox"
                    checked={copyContributions || copyPayments}
                    disabled={copyPayments}
                    onChange={(e) => setCopyContributions(e.target.checked)}
                  />
                  {t('pages.studentDetail.moveGroup.copyContributions')}
                </label>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--k-fg-5b6775)',
                    marginTop: -6,
                  }}
                >
                  {t('pages.studentDetail.moveGroup.copyContributionsHint')}
                </span>

                <L.FormFooter>
                  <L.PrimaryActionButton
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                  >
                    {t('pages.studentDetail.moveGroup.cancel')}
                  </L.PrimaryActionButton>
                  <L.PrimaryActionButton
                    type="submit"
                    tone="primary"
                    disabled={!canSubmit}
                  >
                    {loading
                      ? t('pages.studentDetail.moveGroup.moving')
                      : t('pages.studentDetail.moveGroup.move')}
                  </L.PrimaryActionButton>
                </L.FormFooter>
              </L.FormBody>
            </form>
          )}
        </PopupContainer>
      </Modal>
    );
  },
);
